
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCostEstimator } from '@/context/CostEstimatorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckIcon, SmartphoneIcon, MonitorIcon, GlobeIcon, AppWindowIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const PlatformOption: React.FC<{
  value: string;
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}> = ({ value, label, icon, selected, onClick }) => {
  const { COSTS } = useCostEstimator();
  
  return (
    <div 
      className={cn(
        "border rounded-lg p-6 cursor-pointer transition-all duration-300 hover:shadow-md",
        selected 
          ? "border-primary bg-primary/10" 
          : "border-border hover:border-primary/50"
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl text-primary">{icon}</div>
        {selected && <CheckIcon className="h-5 w-5 text-primary" />}
      </div>
      <h3 className="font-medium text-lg">{label}</h3>
      <p className="text-sm text-muted-foreground mt-1">
        {`$${(COSTS.platforms[value]).toLocaleString()}`}
      </p>
    </div>
  );
};

const PlatformStep: React.FC = () => {
  const { t } = useTranslation();
  const { togglePlatform, options, goToNextStep } = useCostEstimator();
  
  const handleTogglePlatform = (platform: any) => {
    togglePlatform(platform);
  };

  const isNextDisabled = options.platforms.length === 0;

  return (
    <Card className="form-card">
      <CardHeader>
        <CardTitle>{t('platform_title')}</CardTitle>
        <CardDescription>{t('platform_subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <PlatformOption
            value="iOS"
            label={t('platform_ios')}
            icon={<SmartphoneIcon />}
            selected={options.platforms.includes('iOS')}
            onClick={() => handleTogglePlatform('iOS')}
          />
          <PlatformOption
            value="Android"
            label={t('platform_android')}
            icon={<SmartphoneIcon />}
            selected={options.platforms.includes('Android')}
            onClick={() => handleTogglePlatform('Android')}
          />
          <PlatformOption
            value="Web"
            label={t('platform_web')}
            icon={<GlobeIcon />}
            selected={options.platforms.includes('Web')}
            onClick={() => handleTogglePlatform('Web')}
          />
          <PlatformOption
            value="CrossPlatform"
            label={t('platform_cross')}
            icon={<AppWindowIcon />}
            selected={options.platforms.includes('CrossPlatform')}
            onClick={() => handleTogglePlatform('CrossPlatform')}
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={goToNextStep} disabled={isNextDisabled}>
            {t('btn_next')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlatformStep;
